/**
 * Game Logic Service
 *
 * Pure game logic functions with no side effects
 * Contains win condition checks, board validation, etc.
 */

import { Field, StoneType } from "../models";
import { logger } from "./debugLogger";

interface WinAttributes {
  color?: number[];
  size?: number[];
  shape?: number[];
  variant?: number[];
}

const LOG_CONTEXT = { component: "GameLogic" };

/**
 * Check if a stone attribute matches the win condition
 */
const checkAttribute = (
  key: keyof WinAttributes,
  winAttr: WinAttributes,
  stone: StoneType
): WinAttributes => {
  // Add attribute value if:
  // 1. Array is empty (first stone), OR
  // 2. Value is NOT already in array (allows collecting different values)
  if (
    (winAttr && winAttr[key]!.length === 0) ||
    winAttr![key]!.indexOf(stone![key]!) === -1
  ) {
    winAttr[key]!.push(stone![key]!);
  }
  return winAttr;
};

/**
 * Check if the board has a winning condition
 *
 * @param board - The game board
 * @param expertMode - Whether expert mode is enabled (adds 2x2 square wins)
 * @returns false if no win, or array of winning field indices
 */
export const hasWon = (
  board: (Field | null)[],
  expertMode?: boolean | null
): boolean | number[] => {
  logger.debug(
    "Checking for win condition",
    { ...LOG_CONTEXT, method: "hasWon" },
    { expertMode }
  );

  if (!board || board === null) {
    logger.warn("Board is null or undefined", {
      ...LOG_CONTEXT,
      method: "hasWon",
    });
    return false;
  }

  // Standard win rows: horizontal, vertical, diagonal
  const winRows = [
    [0, 1, 2, 3], // Top row
    [4, 5, 6, 7], // Second row
    [8, 9, 10, 11], // Third row
    [12, 13, 14, 15], // Bottom row
    [0, 4, 8, 12], // Left column
    [1, 5, 9, 13], // Second column
    [2, 6, 10, 14], // Third column
    [3, 7, 11, 15], // Right column
    [0, 5, 10, 15], // Diagonal \
    [3, 6, 9, 12], // Diagonal /
  ];

  // Expert mode: add 2x2 square wins
  if (expertMode === true) {
    logger.debug("Expert mode enabled, adding 2x2 square win conditions", {
      ...LOG_CONTEXT,
      method: "hasWon",
    });

    const expertWinRows = [
      [0, 1, 4, 5], // Top-left
      [1, 2, 5, 6], // Top-middle
      [2, 3, 6, 7], // Top-right
      [4, 5, 8, 9], // Middle-left
      [5, 6, 9, 10], // Center
      [6, 7, 10, 11], // Middle-right
      [8, 9, 12, 13], // Bottom-left
      [9, 10, 13, 14], // Bottom-middle
      [10, 11, 14, 15], // Bottom-right
    ];
    winRows.push(...expertWinRows);
  }

  // Check each potential winning row
  for (let i = 0; i < winRows.length; i++) {
    const winRow = winRows[i];

    let winAttr: WinAttributes = {
      color: [],
      size: [],
      shape: [],
      variant: [],
    };

    let allStonesPresent = true;

    // Check all 4 fields in this potential win row
    for (let j = 0; j < winRow.length; j++) {
      const fieldIndex = winRow[j];
      const field = board[fieldIndex];

      // If any field is empty, this row can't be a win
      if (!field?.stone) {
        allStonesPresent = false;
        break;
      }

      const stone = field.stone;

      if (j === 0) {
        // First stone: initialize attributes
        winAttr.color?.push(stone.color!);
        winAttr.size?.push(stone.size!);
        winAttr.shape?.push(stone.shape!);
        winAttr.variant?.push(stone.variant!);
      } else {
        // Subsequent stones: check if they match or differ from previous
        winAttr = checkAttribute("color", winAttr, stone);
        winAttr = checkAttribute("size", winAttr, stone);
        winAttr = checkAttribute("shape", winAttr, stone);
        winAttr = checkAttribute("variant", winAttr, stone);
      }
    }

    // ONLY check win condition if all 4 stones are present!
    if (allStonesPresent) {
      // Check if any attribute has 4 matching (length === 1) or 4 different (length === 4) values
      // length === 1: All 4 stones have the SAME value for this attribute (e.g. all big)
      // length === 4: All 4 stones have DIFFERENT values for this attribute
      if (
        winAttr.color?.length === 1 ||
        winAttr.color?.length === 4 ||
        winAttr.size?.length === 1 ||
        winAttr.size?.length === 4 ||
        winAttr.shape?.length === 1 ||
        winAttr.shape?.length === 4 ||
        winAttr.variant?.length === 1 ||
        winAttr.variant?.length === 4
      ) {
        console.log("🎉🎉🎉 WIN DETECTED!", {
          winRow,
          positions: winRow,
          color: winAttr.color?.length,
          size: winAttr.size?.length,
          shape: winAttr.shape?.length,
          variant: winAttr.variant?.length,
        });

        logger.success(
          "WIN DETECTED!",
          { ...LOG_CONTEXT, method: "hasWon" },
          {
            winRow,
            winAttr,
            expertMode,
          }
        );
        return winRow;
      }
    }
  }

  logger.debug("No win condition found", { ...LOG_CONTEXT, method: "hasWon" });
  return false;
};

/**
 * Get the next player
 */
export const getNextPlayer = (currentPlayer: number): number => {
  return currentPlayer === 1 ? 2 : 1;
};

/**
 * Get player name by player number
 */
export const getPlayerName = (
  playerNumber: number,
  player1Name?: string | null,
  player2Name?: string | null
): string => {
  if (playerNumber === 1) {
    return player1Name || "Player 1";
  } else {
    return player2Name || "Player 2";
  }
};

/**
 * Check if a field is valid for placing a stone
 */
export const isValidPlacement = (
  board: (Field | null)[],
  fieldIndex: number
): boolean => {
  if (fieldIndex < 0 || fieldIndex >= board.length) {
    logger.warn(
      "Invalid field index",
      { ...LOG_CONTEXT, method: "isValidPlacement" },
      { fieldIndex }
    );
    return false;
  }

  if (board[fieldIndex]?.stone) {
    logger.warn(
      "Field already occupied",
      { ...LOG_CONTEXT, method: "isValidPlacement" },
      { fieldIndex }
    );
    return false;
  }

  return true;
};
